<template>
  <div>
    <v-row v-if="isLoading">
      <v-col>
        <div class="text-center ma-6 pa-6">
          <v-progress-circular indeterminate></v-progress-circular>
        </div>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" sm="8">
        <v-card>
          <v-card-title>
            <h3>
              {{ eve.title }}
            </h3>
          </v-card-title>
          <v-card-subtitle class="mt-1">
            <div>
              <v-icon>{{ icons.mdiCalendar }}</v-icon>
              <span class="capitalize">&nbsp;{{ eventTime }}</span>
            </div>
            <div class="mt-2" v-if="eve.enrollment && eve.enrollment_ends">
              <v-icon>{{ icons.mdiLockClock }}</v-icon>&nbsp;{{ $t("Ilmoittautuminen päättyy") }}
              {{ getDayOfWeek(eve.enrollment_ends) }}
              {{ getDateTime(eve.enrollment_ends) }}
            </div>
          </v-card-subtitle>
          <v-card-text class="text--primary mb-0 pb-0">
            <div v-html="markdownToHtml(eve.description)"></div>
            <template v-if="eve.attachments.length">
              <div class="">
                <v-list>
                  <v-list-item v-for="attachment in eve.attachments" :key="`attachment-${attachment.id}`"
                    @click="openAttachment(attachment)">
                    <v-list-item-icon>
                      <v-icon>{{ icons.mdiAttachment }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      {{ attachment.name }}
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </template>
            <v-dialog v-model="isDownloading" hide-overlay persistent width="240">
              <v-card color="primary" dark>
                <v-card-text class="pt-2">
                  {{ $t("Ladataan...") }}

                  <v-progress-linear indeterminate color="white" class="mb-0 mt-2"></v-progress-linear>
                </v-card-text>
              </v-card>
            </v-dialog>
            <v-divider></v-divider>
            <v-row class="mt-1">
              <v-col>
                <v-chip small class="float-right">
                  {{ eve.group_name }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="4" v-if="(eve.enrollment || showEnrollmentCount) && !user.read_only">
        <v-card class="mx-auto">
          <v-card-title>{{ $t("Ilmoittautuminen") }}</v-card-title>
          <v-card-text v-if="!enrollmentExpired">
            <template v-if="enrolled">
              <v-alert text color="success" type="success">
                {{ $t("Olet ilmoittautunut tapahtumaan") }}
              </v-alert>
              <br />
              <v-btn color="secondary" block @click="cancel">
                {{ $t("Peru ilmoittautuminen") }}
              </v-btn>
            </template>
            <template v-else>
              <v-alert text color="info" type="info">
                {{ $t("Et ole ilmoittautunut tapahtumaan") }}
              </v-alert>
              <v-textarea outlined v-if="eve.enrollment_comment" rows="3" v-model="comment"
                placeholder="Lisätieto ilmoittautumiseen"></v-textarea>

              <v-btn color="primary" depressed block @click="enroll">
                {{ $t("Ilmoittaudu mukaan") }}
              </v-btn>
            </template>
          </v-card-text>
          <v-card-text v-else>
            <v-alert text color="info">
              {{ $t("Ilmoittautuminen on päättynyt") }}
            </v-alert>
          </v-card-text>

          <div class="my-3" v-if="showEnrollmentCount">
            <v-divider></v-divider>
            <template v-if="canShowEnrollmentList">
              <div class="px-5 py-2 text-subtitle-1">
                {{ enrollmentCountLabel }}
              </div>
              <v-card-text>
                <div class="enrollment-preview" @click="openEnrollmentsDialog">
                  <v-avatar v-for="(enrollment, index) in enrollmentPreview"
                    :title="`${enrollment.user.last_name} ${enrollment.user.first_name}`"
                    :key="`enrollment-preview-${index}-${enrollment.first_name}`" class="enrollment-preview__avatar"
                    :style="{ zIndex: enrollmentPreview.length + index }">
                    <img v-if="enrollment.user && enrollment.user.picture" :src="enrollment.user.picture.thumbnail"
                      alt="" />
                    <v-icon v-else>{{ icons.mdiAccountCircle }}</v-icon>
                  </v-avatar>
                  <v-avatar v-if="enrollmentPreviewRemaining > 0" color="primary"
                    class="enrollment-preview__avatar enrollment-preview__count white--text" :style="{ zIndex: 10 }">
                    +{{ enrollmentPreviewRemaining }}
                  </v-avatar>
                </div>
              </v-card-text>


            </template>
            <template v-else>
              <v-card outlined>
                <v-card-title>
                  {{ enrollmentCountLabel }}
                </v-card-title>
              </v-card>
            </template>
          </div>

        </v-card>



      </v-col>
    </v-row>

    <v-dialog v-model="showEnrollmentDialog" max-width="520">
      <v-card>
        <v-card-title>
          {{ $t("Ilmoittautuneet") }}
        </v-card-title>
        <v-card-text>
          <v-progress-linear v-if="isLoadingEnrollments" indeterminate class="mb-3"></v-progress-linear>
          <v-list v-else>
            <v-list-item v-for="enrollment in enrollments" :key="`enrollment-${enrollment.id}`">
              <v-list-item-avatar>
                <img v-if="enrollment.user && enrollment.user.picture" :src="enrollment.user.picture.thumbnail"
                  alt="" />
                <v-icon v-else>{{ icons.mdiAccountCircle }}</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{ formatUserName(enrollment.user) }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-if="enrollmentsTotalPages > 1" class="text-center mt-2">
            <v-pagination v-model="enrollmentsPage" :length="enrollmentsTotalPages" />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showEnrollmentDialog = false">{{ $t("Sulje") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api";
import { computed, onMounted, ref, watch } from "vue-demi";
import moment from "moment";
import useAuth from "@/useAuth";
import { useTranslations } from "@/@core/libs/i18n";
import {
  getDateTime,
  getShortDate,
  getDayOfWeek,
  getTime,
} from "@/@core/utils/datetime_helpers";
import useMarkdown from "@/@core/utils/useMarkdown";
import { mdiCalendar, mdiLockClock, mdiAttachment, mdiAccountCircle } from "@mdi/js";
import { i18n } from "@/plugins/i18n";
import download from "downloadjs";

export default {
  props: ["id"],
  setup(props) {
    const isLoading = ref(true);
    const isDownloading = ref(false);
    const eve = ref({});
    const enrolled = ref(false);
    const comment = ref("");
    const enrollments = ref([]);
    const enrollmentPreview = ref([]);
    const isLoadingEnrollments = ref(false);
    const showEnrollmentDialog = ref(false);
    const enrollmentsPage = ref(1);
    const enrollmentsPageSize = ref(50);
    const enrollmentsTotalPages = ref(1);

    const { t } = useTranslations();
    const { tenant, user } = useAuth();
    const { markdownToHtml } = useMarkdown();

    const fetchEvent = async (id) => {
      const response = await api.getEvent(id);
      if (response.ok) {
        if (response.data.enrollment) {
          const enrollmentResponse = await api.getEventEnrollment(id);
          if (enrollmentResponse.ok) {
            enrolled.value = true;
            comment.value = enrollmentResponse.data.comment;
          }
        }

        eve.value = response.data;

        if (canShowEnrollmentList.value) {
          await loadEnrollmentPreview();
        }
      } else {
        alert(response.error);
      }

      isLoading.value = false;
    };

    const loadEnrollments = async () => {
      if (!eve.value?.id || isLoadingEnrollments.value) return;
      isLoadingEnrollments.value = true;
      const response = await api.getEventEnrollments(eve.value.id, enrollmentsPage.value, enrollmentsPageSize.value);
      if (response.ok) {
        enrollments.value = response.data.results || [];
        const count = response.data.count || 0;
        enrollmentsTotalPages.value = Math.max(1, Math.ceil(count / enrollmentsPageSize.value));
      } else {
        alert(response.error);
      }
      isLoadingEnrollments.value = false;
    };

    const loadEnrollmentPreview = async () => {
      if (!eve.value?.id) return;
      const response = await api.getEventEnrollments(eve.value.id, 1, 5);
      if (response.ok) {
        enrollmentPreview.value = response.data.results || [];
      } else {
        enrollmentPreview.value = [];
      }
    };

    const openEnrollmentsDialog = async () => {
      showEnrollmentDialog.value = true;
      enrollmentsPage.value = 1;
      await loadEnrollments();
    };

    onMounted(() => {
      if (props.id) {
        fetchEvent(props.id);
      }
    });

    const enroll = async () => {
      const response = await api.enrollEvent(eve.value.id, comment.value);
      if (response.ok) {
        enrolled.value = true;
      }
      else {
        alert(JSON.stringify(response.error));
      }
    };

    const cancel = async () => {
      const response = await api.cancelEventEnrollment(eve.value.id);
      if (response.ok) {
        enrolled.value = false;
      }
    };

    const enrollmentExpired = computed(() => {
      if (eve.value && eve.value.enrollment_ends) {
        const expires = moment(eve.value.enrollment_ends);
        return expires.isBefore(moment());
      }
      return false;
    });

    const showEnrollmentCount = computed(() => {
      return eve.value?.enrollment_visibility >= 1;
    });

    const canShowEnrollmentList = computed(() => {
      return eve.value?.enrollment_visibility === 2;
    });


    const enrollmentCountLabel = computed(() => {
      const count = eve.value?.enrollment_count || 0;
      if (count === 0) {
        return i18n.t("Ei ilmoittautuneita");
      }
      return `${i18n.t("Ilmoittautuneita:")} ${count}`;
    });

    const enrollmentPreviewRemaining = computed(() => {
      const count = eve.value?.enrollment_count || 0;
      if (count <= 5) {
        return 0;
      }
      return Math.max(0, count - enrollmentPreview.value.length);
    });

    const formatUserName = (user) => {
      if (!user) return "";
      return `${user.last_name || ""} ${user.first_name || ""}`.trim();
    };

    const eventTime = computed(() => {
      if (eve.value.end_time) {
        if (moment(eve.value.time).isSame(eve.value.end_time, "day")) {
          return (
            getDayOfWeek(eve.value.time) +
            " " +
            getShortDate(eve.value.time) + " " +
            i18n.t("klo") + " " +
            getTime(eve.value.time) +
            "-" +
            getTime(eve.value.end_time)
          );
        } else {
          return (
            getDayOfWeek(eve.value.time) +
            " " +
            getShortDate(eve.value.time) +
            " " +
            getTime(eve.value.time) +
            " - " +
            getDayOfWeek(eve.value.end_time) +
            " " +
            getShortDate(eve.value.end_time) +
            " " +
            getTime(eve.value.end_time)
          );
        }
      }
      return getDateTime(eve.value.time);
    });

    const openAttachment = async (attachment) => {
      isDownloading.value = true;
      const response = await api.downloadEventAttachment(
        eve.value.id,
        attachment.id
      );
      isDownloading.value = false;

      if (response.ok) {
        download(response.data, attachment.name);
      } else {
        const error = await response.data.text(); //
        alert(error);
      }
    };

    watch(enrollmentsPage, () => {
      loadEnrollments();
    })

    return {
      isLoading,
      eve,
      enrolled,
      enrollments,
      enrollmentPreview,
      openEnrollmentsDialog,
      showEnrollmentCount,
      canShowEnrollmentList,
      enrollmentCountLabel,
      enrollmentPreviewRemaining,
      formatUserName,
      openAttachment,
      comment,
      eventTime,
      enrollmentExpired,
      enroll,
      cancel,
      t,
      tenant,
      user,
      isLoadingEnrollments,
      showEnrollmentDialog,
      enrollmentsPage,
      enrollmentsTotalPages,
      icons: {
        mdiCalendar,
        mdiLockClock,
        mdiAttachment,
        mdiAccountCircle,
      },
      markdownToHtml,
      getDateTime,
      getTime,
      getDayOfWeek,
      getShortDate,
      isDownloading,
    };
  },
};
</script>

<style>
.enrollment-preview {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.enrollment-preview img {
  width: 40px;
  height: 40px;
}

.enrollment-preview__avatar {
  border: 2px solid #fff;
  margin-left: -14px;
}

.enrollment-preview__count {
  background: #e0e0e0;
  color: #424242;
  font-size: 0.85rem;
  font-weight: 600;
}

.enrollment-preview__avatar:first-child {
  margin-left: 0;
}
</style>
